/**
 * WeatherAssistant — chat-style input backed by Claude (via `/weather-almanac/assistant/ask`).
 *
 * Single-turn (no multi-turn memory yet); each ask is context-rich because
 * the backend injects today's weather + advisories into the system prompt.
 * Signed-in users only.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function WeatherAssistant({ coords }) {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [dateStr, setDateStr] = useState(null);

  useEffect(() => {
    if (!coords) return;
    http.post("/weather-almanac/assistant/suggestions", { lat: coords.lat, lng: coords.lng })
      .then((r) => setSuggestions(r.data.suggestions || []))
      .catch(() => {});
  }, [coords]);

  const ask = async (text) => {
    if (!user) { toast.error("Sign in to use the assistant."); return; }
    const question = (text ?? q).trim();
    if (!question) return;
    setBusy(true); setAnswer(null);
    try {
      const { data } = await http.post("/weather-almanac/assistant/ask", {
        question, lat: coords.lat, lng: coords.lng,
      });
      setAnswer(data.answer);
      setDateStr(data.date);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assistant unavailable right now.");
    } finally { setBusy(false); }
  };

  const onSubmit = (e) => { e.preventDefault(); ask(); };

  return (
    <div className="mt-12" data-testid="weather-assistant">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4" />
        <h2 className="font-display text-2xl">Ask the Weather Assistant</h2>
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--wofy-text-soft)" }}>
        Powered by Claude. Every answer is grounded in your locality's live weather + advisories.
      </p>

      {!user && (
        <div className="rounded-lg border p-4 text-sm" style={{ borderColor: "var(--wofy-border)" }}>
          <Link to="/login" className="underline" style={{ color: "var(--wofy-primary)" }}>Sign in</Link>
          {" "}to ask the assistant.
        </div>
      )}

      {user && (
        <>
          {suggestions.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { setQ(s); ask(s); }}
                  data-testid={`assistant-suggestion-${i}`}
                  className="text-xs rounded-full border px-3 py-1.5 hover:bg-black/[0.03]"
                  style={{ borderColor: "var(--wofy-border)" }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={onSubmit} className="flex items-center gap-2">
            <input
              data-testid="assistant-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. What should I wear for a 5pm run?"
              className="flex-1 rounded-lg border px-3 py-2 text-sm outline-none"
              style={{ borderColor: "var(--wofy-border)" }}
              disabled={busy}
            />
            <button
              data-testid="assistant-submit"
              type="submit"
              disabled={busy || !q.trim()}
              className="wofy-btn-primary text-sm disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {busy ? "Thinking…" : "Ask"}
            </button>
          </form>

          {answer && (
            <div
              className="mt-4 rounded-lg border p-4"
              data-testid="assistant-answer"
              style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
            >
              <div className="text-[10px] uppercase tracking-widest font-mono mb-1" style={{ color: "var(--wofy-text-soft)" }}>
                <Sparkles className="w-3 h-3 inline mr-1" /> Weather Assistant · {dateStr}
              </div>
              <p className="text-sm whitespace-pre-wrap">{answer}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
